import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createItem, deleteItem, getAllItems, getNextDayPlanner, updateItemDate } from "./PlannerService";
import "./NextDayPlanner.css";

const TIME_SLOTS = [
  { value: "MORNING", label: "Morning" },
  { value: "NOON", label: "Noon" },
  { value: "EVENING", label: "Evening" },
  { value: "NIGHT", label: "Night" },
];

function parseDate(raw) {
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(raw) {
  const date = parseDate(raw);
  if (!date) return "-";
  return date.toLocaleDateString();
}

function itemSchedule(item) {
  return item?.dueDate || item?.eventDate || item?.paymentDate || null;
}

function extractSlotFromTitle(title) {
  const match = /^\[(MORNING|NOON|EVENING|NIGHT)\]\s*/i.exec(title || "");
  return match ? match[1].toUpperCase() : null;
}

function stripSlotPrefix(title) {
  return (title || "").replace(/^\[(MORNING|NOON|EVENING|NIGHT)\]\s*/i, "").trim();
}

function timeSlotLabel(slot) {
  return TIME_SLOTS.find((item) => item.value === slot)?.label || "Morning";
}

export default function NextDayPlanner() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingAgenda, setSavingAgenda] = useState(false);
  const [addingSelected, setAddingSelected] = useState(false);
  const [deletingAgendaId, setDeletingAgendaId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [selectedSuggested, setSelectedSuggested] = useState([]);
  const [allPlannerItems, setAllPlannerItems] = useState([]);

  const [agendaText, setAgendaText] = useState("");
  const [agendaSlot, setAgendaSlot] = useState("MORNING");

  const [summary, setSummary] = useState({
    date: "",
    todos: [],
    reminders: [],
    payments: [],
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, allItems] = await Promise.all([getNextDayPlanner(200), getAllItems()]);
      setSummary({
        date: data?.date || "",
        todos: data?.todos || [],
        reminders: data?.reminders || [],
        payments: data?.payments || [],
      });
      setAllPlannerItems(allItems || []);
      setSelectedSuggested([]);
    } catch {
      setError("Unable to load next day planner.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 2500);
    return () => clearTimeout(timer);
  }, [success]);

  const plannedTomorrowIds = useMemo(() => {
    const ids = new Set();
    summary.todos.forEach((item) => ids.add(item.id));
    summary.reminders.forEach((item) => ids.add(item.id));
    summary.payments.forEach((item) => ids.add(item.id));
    return ids;
  }, [summary.todos, summary.reminders, summary.payments]);

  const candidateBase = useMemo(
    () =>
      (allPlannerItems || []).filter((item) => {
        const status = item?.status || "";
        if (status === "DONE" || status === "SKIPPED" || status === "CANCELLED") return false;
        return !plannedTomorrowIds.has(item.id);
      }),
    [allPlannerItems, plannedTomorrowIds]
  );

  const filteredCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidateBase;
    return candidateBase.filter((item) =>
      `${item.title || ""} ${item.type || ""} ${item.category || ""}`.toLowerCase().includes(q)
    );
  }, [candidateBase, query]);

  const agendaBySlot = useMemo(
    () =>
      TIME_SLOTS.reduce((acc, slot) => {
        acc[slot.value] = summary.todos.filter((item) => extractSlotFromTitle(item.title) === slot.value);
        return acc;
      }, {}),
    [summary.todos]
  );

  const toggleSuggested = (id) => {
    setSelectedSuggested((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleAddAgendaNow = async () => {
    const title = agendaText.trim();
    if (!title || savingAgenda) return;

    setSavingAgenda(true);
    setError("");
    setSuccess("");
    try {
      await createItem({
        type: "TODO",
        title: `[${agendaSlot}] ${title}`,
        description: `Planned for ${timeSlotLabel(agendaSlot)} via Next Day Planner`,
        dueDate: summary.date,
        priority: "MEDIUM",
        recurrence: "NONE",
        reminderEnabled: false,
        reminderBeforeDays: 1,
        status: "ACTIVE",
        category: "PERSONAL",
      });
      setAgendaText("");
      setSuccess("Agenda item saved to database.");
      await load();
    } catch {
      setError("Unable to save agenda item.");
    } finally {
      setSavingAgenda(false);
    }
  };

  const handleRemoveAgendaItem = async (id) => {
    if (deletingAgendaId) return;
    setDeletingAgendaId(id);
    setError("");
    setSuccess("");
    try {
      await deleteItem(id);
      setSuccess("Agenda item removed.");
      await load();
    } catch {
      setError("Unable to remove agenda item.");
    } finally {
      setDeletingAgendaId("");
    }
  };

  const handleAddSelectedFromPlanner = async () => {
    if (addingSelected || !selectedSuggested.length) return;
    setAddingSelected(true);
    setError("");
    setSuccess("");
    try {
      await Promise.all(selectedSuggested.map((id) => updateItemDate(id, summary.date, true)));
      setSelectedSuggested([]);
      setSuccess("Selected planner items added to tomorrow.");
      await load();
    } catch {
      setError("Unable to add selected planner items.");
    } finally {
      setAddingSelected(false);
    }
  };

  return (
    <div className="next-day-wrap">
      <div className="next-day-head">
        <div>
          <h2>Plan Your Next Day</h2>
          <p>
            Date: <strong>{formatDate(summary.date)}</strong>
          </p>
        </div>
        <div className="next-day-head-actions">
          <button type="button" onClick={() => nav("/planner")} className="next-day-btn next-day-btn-ghost">
            Back
          </button>
          <button type="button" onClick={load} className="next-day-btn next-day-btn-ghost">
            Refresh
          </button>
        </div>
      </div>

      {loading ? <p>Loading next day planner...</p> : null}
      {error ? <p className="next-day-error">{error}</p> : null}
      {success ? <p className="next-day-success">{success}</p> : null}

      {!loading && (
        <div className="next-day-grid">
          <section className="next-day-panel">
            <h3>Main Agenda</h3>
            <p className="next-day-muted">Add agenda item and time slot. It is saved to database immediately.</p>
            <div className="next-day-agenda-inputs">
              <input
                className="next-day-search"
                value={agendaText}
                onChange={(e) => setAgendaText(e.target.value)}
                placeholder="What do you want to do tomorrow?"
              />
              <select
                className="next-day-slot-select"
                value={agendaSlot}
                onChange={(e) => setAgendaSlot(e.target.value)}
              >
                {TIME_SLOTS.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handleAddAgendaNow} className="next-day-btn" disabled={savingAgenda}>
                {savingAgenda ? "Saving..." : "Add Agenda"}
              </button>
            </div>

            <div className="next-day-slot-grid">
              {TIME_SLOTS.map((slot) => (
                <div key={slot.value} className="next-day-slot-card">
                  <div className="next-day-slot-title">{slot.label}</div>
                  {agendaBySlot[slot.value]?.length ? (
                    agendaBySlot[slot.value].map((item) => (
                      <div key={item.id} className="next-day-draft-item">
                        <span>{stripSlotPrefix(item.title)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAgendaItem(item.id)}
                          disabled={deletingAgendaId === item.id}
                          title="Remove"
                        >
                          x
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="next-day-empty">No items</p>
                  )}
                </div>
              ))}
            </div>

            <div className="next-day-section planned-section">
              <div className="next-day-section-title">
                Other Planned Items ({summary.reminders.length + summary.payments.length})
              </div>
              <div className="next-day-planned-list">
                {summary.reminders.map((item) => (
                  <div key={item.id} className="next-day-chip">
                    <div className="next-day-chip-title">{item.title}</div>
                    <div className="next-day-chip-meta">{item.type}</div>
                  </div>
                ))}
                {summary.payments.map((item) => (
                  <div key={item.id} className="next-day-chip">
                    <div className="next-day-chip-title">{item.title}</div>
                    <div className="next-day-chip-meta">PAYMENT</div>
                  </div>
                ))}
                {summary.reminders.length + summary.payments.length === 0 ? (
                  <p className="next-day-empty">No other items planned.</p>
                ) : null}
              </div>
            </div>
          </section>

          <section className="next-day-panel">
            <h3>Add From Your Planner List</h3>
            <p className="next-day-muted">Pick existing UserPlanner items and add them to tomorrow.</p>

            <div className="next-day-toolbar">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="next-day-search"
                placeholder="Search by title, type, category..."
              />
            </div>

            <div className="next-day-list-summary">
              Candidates {filteredCandidates.length} • Selected {selectedSuggested.length}
            </div>

            <div className="next-day-candidate-list">
              {filteredCandidates.length === 0 ? (
                <p className="next-day-empty">No matching planner items.</p>
              ) : (
                filteredCandidates.map((item) => {
                  const checked = selectedSuggested.includes(item.id);
                  const date = itemSchedule(item);
                  return (
                    <label key={item.id} className={`next-day-candidate ${checked ? "selected" : ""}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleSuggested(item.id)} />
                      <div className="next-day-candidate-content">
                        <div className="next-day-candidate-title">{item.title}</div>
                        <div className="next-day-candidate-meta">
                          <span>{item.type}</span>
                          <span>{item.priority}</span>
                          <span>{item.category || "NO_CATEGORY"}</span>
                          <span>{date ? formatDate(date) : "No date"}</span>
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="next-day-footer">
              <button
                type="button"
                onClick={handleAddSelectedFromPlanner}
                disabled={!selectedSuggested.length || addingSelected}
                className="next-day-btn next-day-btn-secondary"
              >
                {addingSelected ? "Adding..." : `Add ${selectedSuggested.length} Selected Items`}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
